import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LOCALES = ['it', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const FALLBACK_LOCALE: Locale = 'it';

const STORAGE_KEY = 'sentinel.locale';

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null && (SUPPORTED_LOCALES as readonly string[]).includes(stored))
      return stored as Locale;
  } catch {
    /* storage unavailable */
  }
  const configured = import.meta.env.VITE_DEFAULT_LOCALE;
  return (SUPPORTED_LOCALES as readonly string[]).includes(configured)
    ? (configured as Locale)
    : FALLBACK_LOCALE;
}

/** The translations of an app, one tree per locale. */
export type Translations = Record<Locale, Record<string, unknown>>;

/** Every interface string comes from here. Keys are `module.context.key`; both languages
 *  ship in the same commit (a parity test enforces it). Each app brings its own tree. */
export function setupI18n({ it, en }: Translations): typeof i18next {
  if (!i18next.isInitialized) {
    void i18next.use(initReactI18next).init({
      resources: { it: { translation: it }, en: { translation: en } },
      lng: initialLocale(),
      fallbackLng: FALLBACK_LOCALE,
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  }
  return i18next;
}

export async function changeLocale(locale: Locale): Promise<void> {
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* storage unavailable */
  }
}

export function currentLocale(): Locale {
  const lng = i18next.language;
  return (SUPPORTED_LOCALES as readonly string[]).includes(lng) ? (lng as Locale) : FALLBACK_LOCALE;
}
